import { zodResolver } from '@hookform/resolvers/zod';
import { isEmpty } from 'rambda';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import ProgressButton from '@/components/common/progress-button';
import {
  CreateGroupForm,
  type CreateGroupFormData,
  createGroupFormScheme,
} from '@/components/contacts/create-group-form';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import useUploadFile from '@/hooks/files/use-upload-file';
import useCreateGroup from '@/hooks/groups/use-create-group';
import ROUTES from '@/routes';
import { getUserId } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';

export default function CreateGroupRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const { upload, isLoading: isUploading } = useUploadFile();
  const { mutateAsync: createGroup, isPending } = useCreateGroup();
  const { getFileSizeLimit } = useFileSizeLimit();
  const { pathname } = useLocation();

  const form = useForm<CreateGroupFormData>({
    resolver: zodResolver(createGroupFormScheme),
    defaultValues: {
      UserIDs: [],
      GroupName: '',
    },
  });

  const memberIds = form.watch('UserIDs', []);

  const handleCreateGroup = async (data: CreateGroupFormData) => {
    const userId = getUserId();
    if (!userId) return Promise.reject();

    const limit = getFileSizeLimit('image');

    if (data.GroupImg) {
      if (data.GroupImg.size > limit) {
        addToast({
          type: 'error',
          message: t('errors.file-size-limit', { limit: readableFileSize(limit) }),
        });
        return Promise.reject('FILE_TOO_LARGE');
      }
      const uploadResult = await upload(data.GroupImg);
      data.GroupImg.url = uploadResult.FileUrl;
    }

    const result = await createGroup({
      GroupName: data.GroupName,
      GroupImg: data.GroupImg?.url,
      UserIDs: isEmpty(data.UserIDs) ? `${userId}|0` : data.UserIDs.concat([userId]).join('|'),
    });

    if (result?.Code === 0 && result.Data) {
      return navigate(ROUTES.MESSAGES.$path());
    }
  };

  return (
    <PageLayout
      className='w-full'
      title={step === 0 ? t('conversation.list.menu.select-contact') : t('conversation.list.menu.create-group')}
      headerConfig={{
        navigateBack:
          step === 0
            ? true
            : () => {
                setStep(0);
                navigate(pathname, { state: { step: 0 } });
              },
        actions: step ? (
          <ProgressButton
            as='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            isLoading={isPending || isUploading}
            onClick={form.handleSubmit(handleCreateGroup, console.error)}
            label='contact.create-group.create'
          />
        ) : (
          <ProgressButton
            as='button'
            className='btn btn-ghost btn-sm text-primary mode-embedded:text-primary-content'
            disabled={memberIds.length < 1}
            onClick={() => {
              setStep(1);
              navigate(pathname, { state: { step: 1 } });
            }}
            label='common.confirm'
          />
        ),
      }}>
      <CreateGroupForm
        step={step}
        form={form}
      />
    </PageLayout>
  );
}
